import React from 'react';
import { Link } from 'react-router-dom';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { useOrgFeature } from '@/hooks/useOrgFeature';

interface RequireOrgFeatureProps {
  feature: string;
  children: React.ReactNode;
}

// Guards a route behind a per-organization opt-in add-on (e.g. 'newsroom')
// so a direct/bookmarked URL doesn't reach a page whose API calls will just
// 403 — shows a friendly placeholder instead. Complements
// requireOrgFeatureEnabled on the backend and the nav-hiding in Layout.tsx,
// which only hides the entry point, not the route. Same shape as
// RequireFeatureFlag, but for per-org (not platform-wide) gates.
const RequireOrgFeature: React.FC<RequireOrgFeatureProps> = ({ feature, children }) => {
  const { isEnabled, isLoading } = useOrgFeature(feature);

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isEnabled) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
        <div className="h-14 w-14 rounded-2xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-4">
          <SparklesIcon className="h-7 w-7 text-blue-600 dark:text-blue-400" />
        </div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">This add-on isn't enabled for your organization</h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
          Contact your administrator if you'd like this feature turned on.
        </p>
        <Link
          to="/app"
          className="mt-6 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
        >
          Back to Dashboard
        </Link>
      </div>
    );
  }

  return <>{children}</>;
};

export default RequireOrgFeature;
